import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { useConfigureBankAccountMutation } from "#graphql/supergraphHooks";
import {
  ConfigureBankAccountInput,
  ConfigureBankAccountMutation,
} from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";

export interface UseConfigureBankAccountOptions {
  onSuccess?: (data: ConfigureBankAccountMutation) => void;
  onError?: (error: Error) => void;
}

export interface UseConfigureBankAccountReturn {
  configureBankAccount: (input: ConfigureBankAccountInput) => Promise<boolean>;
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * Hook that handles configuring bank account settings including providers,
 * verifications, and FundRecs configurations.
 *
 * Uses declarative state submission:
 * - null = no changes to this section
 * - [] = remove all items from this section
 * - [...items] = declarative state - sync to match this list
 *
 * @example
 * ```tsx
 * const { configureBankAccount, loading } = useConfigureBankAccount({
 *   onSuccess: (data) => console.log('Configuration saved', data),
 * });
 *
 * await configureBankAccount({
 *   bankAccountId: 123,
 *   configurations: [
 *     { providerCapabilityId: null, fundRecsExportStatus: 1, fundRecsUdf: "MY_UDF" }
 *   ],
 * });
 * ```
 */
export const useConfigureBankAccount = (
  options: UseConfigureBankAccountOptions = {},
): UseConfigureBankAccountReturn => {
  const { onSuccess, onError } = options;

  const [configureBankAccountMutation, { loading, error }] = useConfigureBankAccountMutation({
    context: clientContext(),
  });

  // Report underlying Apollo/GraphQL errors to Datadog. Consumers may also report
  // synthetic "returned false" messages — these provide the underlying details.
  useReportError(error ? formatError(error) : null);

  const configureBankAccount = useCallback(
    async (input: ConfigureBankAccountInput): Promise<boolean> => {
      try {
        const result = await configureBankAccountMutation({
          variables: { configureBankAccountInput: input },
        });

        const data = result.data;
        if (data?.configureBankAccount?.success) {
          onSuccess?.(data);
          return true;
        } else {
          return false;
        }
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error("An unexpected error occurred"));
        return false;
      }
    },
    [configureBankAccountMutation, onSuccess, onError],
  );

  return {
    configureBankAccount,
    loading,
    error,
  };
};
